import type { Recipe } from "../models/recipe";
import type { Ingredient } from "../models/ingredient";
import { shoppingItemKey } from "./shoppingList";

// "What can I make?" — match a set of ingredients you have on hand against your
// recipes, ranked by how few extra items you'd need to buy.

export interface RecipeIngredientMatch {
  recipe: Recipe;
  /** Recipe ingredients matched to what YOU entered (length === usesCount). */
  have: Ingredient[];
  /** Recipe ingredient names assumed on-hand as staples (only when assumeStaples). */
  staples: string[];
  /** Recipe ingredients you'd still need to buy (full objects → add-to-list ready). */
  missing: Ingredient[];
  /** How many ingredients match what YOU entered (excludes assumed staples). */
  usesCount: number;
  /** Of those, how many matched EXACTLY (not via a looser alias/subset) — used to
   *  rank recipes that literally contain your ingredient above alias matches. */
  exactCount: number;
}

// Words to ignore when comparing ingredient names, so "fresh large eggs" and "egg"
// still line up. `shoppingItemKey` already lowercases + singularises the last word.
const MATCH_STOPWORDS = new Set([
  "of", "the", "and", "or", "a", "an", "to", "for", "with", "in",
  "fresh", "large", "small", "medium", "chopped", "diced", "sliced", "minced",
  "grated", "ground", "whole", "ripe", "dried", "frozen", "raw", "cooked",
]);

// Things most kitchens always have — so a recipe doesn't read as "missing" just
// because it needs salt. Matched by the head (last) word, so "sea salt", "olive
// oil", "caster sugar" all count. Can be turned off by the caller.
const STAPLE_HEADS = new Set(["salt", "pepper", "water", "oil", "butter", "sugar", "flour"]);

// Some staple heads are ALSO the head of common non-staple ingredients — "bell
// pepper"/"capsicum … pepper" isn't the pepper shaker, "peanut butter" isn't
// butter. For these, a head match only counts as a staple when every other word
// is a recognised seasoning/prep qualifier; anything else (bell, capsicum,
// peanut, red…) means it's a different ingredient that just ends the same way.
const QUALIFIED_STAPLE_HEADS: Record<string, Set<string>> = {
  pepper: new Set(["black", "white", "cracked", "coarse", "fine", "mixed"]),
  butter: new Set(["unsalted", "salted", "melted", "softened"]),
};

// Reduce a messy ingredient line to a clean-ish name for matching + display.
// Imported recipes often store the whole line in `name` (e.g. "(185 grams)
// butter, unsalted, softened to 18°C / 64°F; (Note 4)") → "butter". Heuristic
// (ingredient lines are irregular): drop a leading (quantity), keep the part
// before the first comma/semicolon, drop a trailing (note/alt name).
export function cleanIngredientName(name: string): string {
  let s = name.trim();
  s = s.replace(/^\([^)]*\)\s*/, ""); // leading "(185 grams) "
  s = s.split(/[,;]/)[0]; // "butter, unsalted, …" → "butter"
  s = s.replace(/\s*\([^)]*\)\s*$/, ""); // trailing "(superfine sugar)" / "(Note 4)"
  return s.trim() || name.trim();
}

function contentTokens(key: string): string[] {
  return key.split(" ").filter((t) => t && !MATCH_STOPWORDS.has(t));
}

function isStaple(key: string): boolean {
  const parts = contentTokens(key);
  if (!parts.length) return false;
  const head = parts[parts.length - 1];
  if (!STAPLE_HEADS.has(head)) return false;
  // Ambiguous heads (pepper, butter) are staples only when every other word is a
  // seasoning/prep qualifier — so "black pepper" counts but "bell pepper" and
  // "capsicum / bell pepper" don't.
  const qualifiers = QUALIFIED_STAPLE_HEADS[head];
  if (qualifiers) return parts.slice(0, -1).every((t) => qualifiers.has(t));
  return true;
}

// Classify how a recipe ingredient matches what you have:
//  - "exact": same key, or the same content tokens ignoring stopwords — you
//    entered "basmati rice" and the recipe lists "basmati rice".
//  - "fuzzy": one is a proper token-subset of the other ("rice" ↔ "basmati rice",
//    "chicken" ↔ "chicken thigh") — a looser, alias-style match.
//  - null: no match.
// Exact matches rank a recipe above fuzzy ones (see the sort below).
function matchKind(recipeKey: string, haveKeys: string[]): "exact" | "fuzzy" | null {
  const rTok = contentTokens(recipeKey);
  const rSet = new Set(rTok);
  let fuzzy = false;
  for (const h of haveKeys) {
    if (h === recipeKey) return "exact"; // literal key equality
    const hTok = contentTokens(h);
    if (!hTok.length || !rTok.length) continue;
    const hSet = new Set(hTok);
    const haveSubRecipe = hTok.every((t) => rSet.has(t)); // have ⊆ recipe
    const recipeSubHave = rTok.every((t) => hSet.has(t)); // recipe ⊆ have
    if (haveSubRecipe && recipeSubHave) return "exact"; // same content tokens
    if (haveSubRecipe || recipeSubHave) fuzzy = true;
  }
  return fuzzy ? "fuzzy" : null;
}

export function matchRecipesByIngredients(
  have: string[],
  recipes: Recipe[],
  ingredients: Ingredient[],
  opts: { assumeStaples?: boolean } = {},
): RecipeIngredientMatch[] {
  const assumeStaples = opts.assumeStaples ?? true;
  const haveKeys = [
    ...new Set(have.map((h) => shoppingItemKey(cleanIngredientName(h))).filter(Boolean)),
  ];
  if (haveKeys.length === 0) return [];

  const byRecipe = new Map<string, Ingredient[]>();
  for (const ing of ingredients) {
    if (ing.group || !ing.name?.trim()) continue; // skip section headings + blanks
    const list = byRecipe.get(ing.recipeId);
    if (list) list.push(ing);
    else byRecipe.set(ing.recipeId, [ing]);
  }

  const results: RecipeIngredientMatch[] = [];
  for (const recipe of recipes) {
    const ings = byRecipe.get(recipe.id);
    if (!ings || ings.length === 0) continue;

    const have: Ingredient[] = [];
    const staples: string[] = [];
    const missing: Ingredient[] = [];
    let usesCount = 0;
    let exactCount = 0;
    for (const ing of ings) {
      const key = shoppingItemKey(cleanIngredientName(ing.name));
      const kind = matchKind(key, haveKeys);
      if (kind) {
        have.push(ing);
        usesCount++;
        if (kind === "exact") exactCount++;
      } else if (assumeStaples && isStaple(key)) {
        staples.push(ing.name);
      } else {
        missing.push(ing);
      }
    }

    if (usesCount === 0) continue; // must actually use one of your ingredients
    results.push({ recipe, have, staples, missing, usesCount, exactCount });
  }

  // Most of YOUR ingredients used first (adding an ingredient only ever moves a
  // recipe that uses it up); then recipes that match your ingredient EXACTLY
  // above ones that only match via a looser alias ("rice" for "basmati rice");
  // then closest-to-cookable (fewest still to buy).
  results.sort(
    (a, b) =>
      b.usesCount - a.usesCount ||
      b.exactCount - a.exactCount ||
      a.missing.length - b.missing.length ||
      a.recipe.title.localeCompare(b.recipe.title),
  );
  return results;
}
